// Inteligencia › Seguridad e Higiene (IA Pro, ex pestaña "IA").
// Todo lo que se muestra está VERIFICADO:
// - El modelo de obra (pirIaAnalyzes) solo elige qué fotos y qué personas mirar.
// - Claude revisa cada persona una por una (backend /api/seguridad/:feedId/verificar): ¿es una persona?,
//   ¿casco y chaleco? Se cuenta solo lo confirmado con certeza alta, y cada
//   elemento solo donde se veía. La verificación de los días que faltan se dispara al abrir la pantalla.
// - Personas en obra: reporte diario con Re-ID (lisual-identity/reporte_nocturno.py), recorte de cada persona,
//   primera y última vez que se la vio y permanencia.
// - Zonas de alto riesgo: polígonos que marca el cliente (/api/seguridad/:feedId/config).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import {
  HardHat, Users, Clock, AlertTriangle, ShieldCheck, ShieldAlert, Shirt, MapPin, Bell, Loader2,
  Download, Pencil, FileText, UserRound, LogIn, LogOut, Info, CheckCircle2, XCircle, ScanSearch, BadgeCheck,
} from 'lucide-react';
import { addDays, todayKey, fmtHM, fmtKey, fmtDow, photoUrl } from '../IADashboard/iaData';
import { getConfig, getPersonas, getVerificado, verificarDia, guardarConfig, errorSeguridad } from './seguridad.api';
import { analizar, recomendaciones, fmtHoras, fmtPermanencia, ELEMENTOS, elementosDe, pct, diaDe, confirmada, MIN_MUESTRA } from './seguridadData';
import ZonasRiesgoEditor, { COLOR_NIVEL } from './ZonasRiesgoEditor';

const PERIODOS = [
  { key: 'hoy', label: 'Hoy', dias: () => [todayKey()] },
  { key: 'ayer', label: 'Ayer', dias: () => [addDays(todayKey(), -1)] },
  { key: '7', label: '7 días', dias: () => Array.from({ length: 7 }, (_, i) => addDays(todayKey(), i - 6)) },
  { key: '30', label: '30 días', dias: () => Array.from({ length: 30 }, (_, i) => addDays(todayKey(), i - 29)) },
];
const HORAS_VISTA = Array.from({ length: 12 }, (_, i) => i + 7); // 7 a 18 h
const ICONO = { casco: HardHat, chaleco: Shirt };
const r0 = (n) => (n == null ? '—' : Math.round(n));
const colorCumpl = (v, meta) => (v == null ? '#e5e7eb' : v >= meta ? '#10b981' : v >= meta - 15 ? '#f59e0b' : '#ef4444');

// ---------------------------------------------------------------------------
// Piezas chicas
// ---------------------------------------------------------------------------
const Tarjeta = ({ titulo, icono: Icono, derecha, children, className = '', ...rest }) => (
  <section className={`bg-white border border-gray-200 rounded-2xl shadow-sm min-w-0 ${className}`} {...rest}>
    {titulo && (
      <header className="flex items-center justify-between gap-2 flex-wrap px-4 py-3 border-b border-gray-100">
        <h3 className="flex items-center gap-2 text-sm sm:text-[15px] font-semibold text-gray-800">{Icono && <Icono size={17} className="text-gray-500" />}{titulo}</h3>
        {derecha && <div className="flex items-center gap-2 flex-wrap">{derecha}</div>}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ icono: Icono, titulo, valor, sub, tono = 'gris' }) => {
  const tonos = { verde: 'text-emerald-700 bg-emerald-50', rojo: 'text-red-700 bg-red-50', ambar: 'text-amber-700 bg-amber-50', gris: 'text-gray-700 bg-gray-100', azul: 'text-blue-700 bg-blue-50' };
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 shadow-sm min-w-0">
      <div className="flex items-center gap-2 text-xs text-gray-500"><span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${tonos[tono]}`}><Icono size={15} /></span><span className="leading-tight">{titulo}</span></div>
      <div className="mt-2 text-2xl sm:text-[28px] font-bold text-gray-900 leading-none">{valor}</div>
      {sub && <div className="mt-1.5 text-[11px] sm:text-xs text-gray-500 leading-snug">{sub}</div>}
    </div>
  );
};

const Chip = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick} className={`text-xs sm:text-sm font-semibold rounded-full px-3 py-1.5 border transition-colors ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>{children}</button>
);

const BadgeCasco = ({ pct: v, meta }) => {
  if (v == null) return <span className="text-xs text-gray-400">—</span>;
  const c = colorCumpl(v, meta);
  return <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5" style={{ color: c, background: c + '1a' }}><HardHat size={12} />{v}%</span>;
};

/** Recorte de una persona sobre la foto (miniatura firmada), encuadre 3:4 con la persona entera. */
function Recorte({ foto, box, borde }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivo = true;
    photoUrl(foto, { w: 1280 }).then((u) => vivo && setUrl(u)).catch(() => {});
    return () => { vivo = false; };
  }, [foto]);
  const [x1, y1, x2, y2] = box;
  const W = foto.w, H = foto.h;
  const w0 = Math.min(W, Math.max((x2 - x1) * 1.5, ((y2 - y1) * 1.3 * 3) / 4)), h0 = Math.min(H, (w0 * 4) / 3);
  const left = Math.min(Math.max(0, (x1 + x2) / 2 - w0 / 2), W - w0), top = Math.min(Math.max(0, (y1 + y2) / 2 - h0 / 2), H - h0);
  return (
    <div className="relative w-full bg-gray-800 overflow-hidden" style={{ aspectRatio: '3 / 4', borderBottom: `3px solid ${borde}` }}>
      {url ? (
        <div className="absolute inset-0" style={{
          backgroundImage: `url("${url}")`, backgroundRepeat: 'no-repeat',
          backgroundSize: `${(W / w0) * 100}% ${(H / h0) * 100}%`,
          backgroundPosition: `${W > w0 ? (left / (W - w0)) * 100 : 0}% ${H > h0 ? (top / (H - h0)) * 100 : 0}%`,
        }} />
      ) : <div className="absolute inset-0 grid place-items-center text-gray-400 text-[11px]">Cargando…</div>}
      {/* La persona evaluada: en el recorte puede haber otras */}
      <div className="absolute rounded-sm" style={{ left: `${((x1 - left) / w0) * 100}%`, top: `${((y1 - top) / h0) * 100}%`, width: `${((x2 - x1) / w0) * 100}%`, height: `${((y2 - y1) / h0) * 100}%`, border: `2px solid ${borde}`, boxShadow: '0 0 0 1px rgba(255,255,255,.7)' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pantalla
// ---------------------------------------------------------------------------
export default function SeguridadHigiene({ customerId, cam, name, feedId, onIrA }) {
  const [periodo, setPeriodo] = useState('7');
  const [verif, setVerif] = useState(null); // { dias: [...] }
  const [errorVerif, setErrorVerif] = useState(null);
  const [progreso, setProgreso] = useState({}); // dia -> { total, hechas }
  const [verificando, setVerificando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [config, setConfig] = useState({ zonasRiesgo: [], metaCasco: 90, obligatorios: { casco: true, chaleco: true } });
  const [guardandoOblig, setGuardandoOblig] = useState(false);
  const [personas, setPersonas] = useState(null);
  const [errorPersonas, setErrorPersonas] = useState(false);
  const [diaPersonas, setDiaPersonas] = useState(null);
  const [editor, setEditor] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [verEventos, setVerEventos] = useState(12);
  const [elemento, setElemento] = useState('casco');
  const [fotoZonasUrl, setFotoZonasUrl] = useState(null);
  const dias = useMemo(() => PERIODOS.find((p) => p.key === periodo).dias(), [periodo]);
  const meta = config.metaCasco ?? 90;
  const vivoRef = useRef(0);

  // Lo verificado del período y, en segundo plano, verificar lo que falte (del día más nuevo al más viejo)
  useEffect(() => {
    const yo = ++vivoRef.current;
    const vivo = () => vivoRef.current === yo;
    setVerif(null); setErrorVerif(null); setProgreso({}); setAviso(null); setVerEventos(12);
    if (!feedId) return undefined;
    const desde = dias[0], hasta = dias[dias.length - 1];
    const recargar = () => getVerificado(feedId, desde, hasta).then((r) => vivo() && setVerif(r));
    (async () => {
      try { await recargar(); } catch (e) { if (vivo()) setErrorVerif(errorSeguridad(e)); return; }
      setVerificando(true);
      try {
        // Los días anteriores los deja listos la verificación en segundo plano (cada hora): desde la pantalla solo
        // se completa lo más nuevo (hoy y ayer), que puede tener fotos recién llegadas.
        const ayer = addDays(todayKey(), -1);
        for (const dia of [...dias].reverse().filter((d) => d >= ayer)) {
          for (let i = 0, fallas = 0; i < 25 && vivo(); i += 1) {
            let r;
            try {
              r = await verificarDia(feedId, dia);
              fallas = 0;
            } catch (e) {
              // Corte de red o error del servidor: lo ya verificado quedó guardado; se reintenta un par de veces.
              const st = e?.response?.status;
              if ((st && st < 500) || fallas >= 3) throw e;
              fallas += 1;
              await new Promise((ok) => setTimeout(ok, 3000 * fallas));
              continue;
            }
            if (!vivo()) return;
            setProgreso((p) => ({ ...p, [dia]: { total: r.total, hechas: r.hechas } }));
            if (r.sinPresupuesto) { setAviso('Llegamos al tope de verificaciones del mes. Lo que falta se verifica el mes que viene.'); return; }
            if (r.sinClave) { setAviso('La verificación con IA no está disponible en este momento.'); return; }
            if (r.hechas) await recargar();
            if (!r.pendientes) break;
            // Otro pedido está verificando (esta u otra cámara): se espera el turno y se sigue.
            if (r.ocupado) await new Promise((ok) => setTimeout(ok, 5000));
          }
        }
      } catch (e) {
        if (vivo()) setAviso(e?.response?.status === 403 ? 'La verificación con IA es parte de IA Pro.' : `No pudimos terminar de verificar: ${errorSeguridad(e)}`);
      } finally {
        if (vivo()) { setVerificando(false); recargar().catch(() => {}); }
      }
    })();
    return () => { vivoRef.current += 1; };
  }, [feedId, dias]);

  // Config (zonas de riesgo y meta) y reportes de personas de los últimos 62 días
  useEffect(() => {
    let vivo = true;
    if (!feedId) return undefined;
    getConfig(feedId).then((c) => vivo && setConfig(c)).catch(() => {});
    getPersonas(feedId, addDays(todayKey(), -61), todayKey())
      .then((r) => vivo && setPersonas(r))
      .catch(() => { if (vivo) { setErrorPersonas(true); setPersonas({ dias: [] }); } });
    return () => { vivo = false; };
  }, [feedId]);

  const obligatorios = config.obligatorios || { casco: true, chaleco: true };
  const visibles = elementosDe(obligatorios);
  const a = useMemo(() => (verif ? analizar(verif.dias || [], config.zonasRiesgo || [], config.obligatorios) : null), [verif, config]);
  // Si el elemento elegido deja de ser obligatorio, se pasa al otro.
  useEffect(() => { if (visibles.length && !visibles.some((e) => e.key === elemento)) setElemento(visibles[0].key); }, [visibles, elemento]);
  const cambiarObligatorio = async (key) => {
    const nuevo = { ...obligatorios, [key]: !obligatorios[key] };
    const antes = config;
    setConfig({ ...config, obligatorios: nuevo });
    setGuardandoOblig(true);
    try { setConfig(await guardarConfig(feedId, { zonasRiesgo: config.zonasRiesgo || [], metaCasco: config.metaCasco ?? 90, obligatorios: nuevo })); }
    catch (e) { setConfig(antes); setAviso(`No pudimos guardar qué es obligatorio: ${errorSeguridad(e)}`); }
    finally { setGuardandoOblig(false); }
  };
  const prog = Object.values(progreso).reduce((s, x) => ({ total: s.total + x.total, hechas: s.hechas + x.hechas }), { total: 0, hechas: 0 });

  // Reporte de personas (Re-ID): el último dentro del período (o el último que haya)
  const reportes = personas?.dias || [];
  const enPeriodo = reportes.filter((d) => d.fecha >= dias[0] && d.fecha <= dias[dias.length - 1]);
  const ultimoReporte = reportes[reportes.length - 1] || null;
  const rep = reportes.find((d) => d.fecha === diaPersonas) || enPeriodo[enPeriodo.length - 1] || null;
  const resumenPersonas = useMemo(() => {
    if (!rep) return null;
    const ps = rep.personas || [];
    return {
      fecha: fmtKey(rep.fecha),
      total: ps.length,
      siempreCasco: ps.filter((p) => p.cascoPct === 100).length,
      sinCascoSiempre: ps.filter((p) => p.cascoPct === 0).length,
      permanenciaProm: ps.length ? Math.round(ps.reduce((s, p) => s + (p.permanenciaMin || 0), 0) / ps.length) : null,
    };
  }, [rep]);
  const consejos = useMemo(() => recomendaciones(a, meta, { personasDia: resumenPersonas, obligatorios: config.obligatorios }), [a, meta, resumenPersonas, config.obligatorios]);

  // Foto para las zonas de riesgo: la de más gente verificada (o la última verificada)
  const fotoZonas = a?.picoFoto || (verif?.dias || []).flatMap((d) => d.fotos).slice(-1)[0] || null;
  useEffect(() => {
    let vivo = true;
    setFotoZonasUrl(null);
    if (fotoZonas) photoUrl(fotoZonas, { w: 1280 }).then((u) => vivo && setFotoZonasUrl(u)).catch(() => {});
    return () => { vivo = false; };
  }, [fotoZonas]);

  const eventos = (a?.eventos || []).filter((e) => (filtro === 'todos' ? true : filtro === 'zona' ? !!e.zona : e.faltan.includes(filtro)));
  const cargando = !verif && !errorVerif;
  const unDia = dias.length === 1;
  const el = ELEMENTOS.find((e) => e.key === elemento);

  const descargarCsv = () => {
    if (!a) return;
    const filas = [['Fecha', 'Fotos verificadas', 'Personas confirmadas', 'Pico', 'Horas-persona', ...visibles.map((e) => `${e.label} %`), 'Fotos con falta de EPP', 'Horas-persona en zonas de riesgo']];
    a.dias.forEach((d) => filas.push([d.key, d.fotos, d.personas, d.pico, d.hp.toFixed(1), ...visibles.map((e) => { const v = pct(d.c[e.key]); return v == null ? '' : Math.round(v); }), d.fotosConFalta, d.hpRiesgo.toFixed(1)]));
    const blob = new Blob(['﻿' + filas.map((x) => x.join(';')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `seguridad-${cam}-${dias[0]}_${dias[dias.length - 1]}.csv`; link.click();
  };

  const datosGrafico = a ? (unDia
    ? HORAS_VISTA.map((h) => ({ x: `${h}h`, v: pct(a.horas[h][elemento], 2) == null ? null : Math.round(pct(a.horas[h][elemento], 2)), n: a.horas[h][elemento].si + a.horas[h][elemento].no }))
    // Todos los días del período, aunque no tengan datos (barra vacía): así "30 días" muestra los 30.
    : dias.map((k) => { const d = a.dias.find((x) => x.key === k); const v = d ? pct(d.c[elemento], 2) : null; return { x: `${fmtDow(k)} ${k.slice(8)}`, v: v == null ? null : Math.round(v), n: d ? d.c[elemento].si + d.c[elemento].no : 0 }; })) : [];

  return (
    <div className="space-y-4 sm:space-y-5" data-seguridad>
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="text-emerald-600 shrink-0" size={26} />Seguridad e Higiene</h2>
          <p className="text-sm text-gray-500 mt-0.5">{name ? `${name} · ` : ''}Uso de casco y chaleco, personas en obra y zonas de riesgo.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {PERIODOS.map((p) => <Chip key={p.key} on={periodo === p.key} onClick={() => { setPeriodo(p.key); setDiaPersonas(null); }}>{p.label}</Chip>)}
        </div>
      </div>

      {/* Verificación */}
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-seg-verificacion>
        <BadgeCheck size={20} className="text-emerald-600 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-950 min-w-0 flex-1">
          <b>Datos verificados.</b> Cada persona detectada se revisa una por una con IA. Solo contamos a quien es una persona con certeza, y cada elemento de seguridad solo cuando se ve bien.
          {a && a.descartadas > 0 && <> Este período se descartaron <b>{a.descartadas}</b> detecciones que no eran personas.</>}
          {verificando && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-xs text-emerald-800"><Loader2 size={13} className="animate-spin" />Verificando fotos nuevas… {prog.total ? `${prog.hechas} de ${prog.total}` : ''}</div>
              {prog.total > 0 && <div className="mt-1 h-1.5 rounded-full bg-emerald-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${(100 * prog.hechas) / prog.total}%` }} /></div>}
            </div>
          )}
          {aviso && <div className="mt-1 text-xs text-amber-800">{aviso}</div>}
        </div>
      </div>

      <div className="flex items-center gap-x-3 gap-y-2 flex-wrap bg-white border border-gray-200 rounded-2xl px-4 py-3" data-seg-obligatorios>
        <span className="text-sm font-semibold text-gray-700">En esta obra es obligatorio:</span>
        {ELEMENTOS.map((e) => {
          const I = ICONO[e.key], on = obligatorios[e.key] !== false;
          return (
            <label key={e.key} className={`inline-flex items-center gap-1.5 text-sm rounded-full border px-3 py-1.5 cursor-pointer select-none ${on ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-gray-200 text-gray-500'}`}>
              <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={on} disabled={guardandoOblig} onChange={() => cambiarObligatorio(e.key)} data-seg-oblig={e.key} />
              <I size={15} />{e.label}
            </label>
          );
        })}
        <span className="text-[11px] text-gray-400">Lo que no es obligatorio no se muestra ni cuenta como falta.</span>
      </div>

      {cargando && <div className="flex items-center gap-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-2xl px-4 py-3"><Loader2 size={16} className="animate-spin" />Cargando…</div>}
      {errorVerif && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">No pudimos leer los datos verificados: {errorVerif}</div>}

      {/* Indicadores */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
        <Kpi icono={Users} titulo="Personas confirmadas" tono="azul" valor={a ? a.personas : '—'} sub={a ? `En ${a.fotos} fotos revisadas` : ''} />
        <Kpi icono={UserRound} titulo="Pico de personas" tono="azul" valor={a ? a.pico : '—'} sub={a?.picoFoto ? `En una foto · ${unDia ? '' : `${fmtDow(diaDe(a.picoFoto.ms))} ${fmtKey(diaDe(a.picoFoto.ms)).slice(0, 5)} `}${fmtHM(a.picoFoto.ms)} h` : 'En una misma foto'} />
        <Kpi icono={Clock} titulo="Horas-persona" tono="gris" valor={a ? fmtHoras(a.horasPersona) : '—'} sub="Tiempo de gente en obra" />
        {visibles[0] ? (() => { const k = visibles[0].key, v = pct(a?.total[k], MIN_MUESTRA); return <Kpi icono={ICONO[k]} titulo={`Uso de ${visibles[0].label.toLowerCase()}`} tono={v == null ? 'gris' : v >= meta ? 'verde' : 'rojo'} valor={v == null ? '—' : `${r0(v)}%`} sub={`Meta ${meta}%`} />; })() : <Kpi icono={ShieldCheck} titulo="EPP obligatorio" tono="gris" valor="—" sub="Marcá qué es obligatorio" />}
        <Kpi icono={ShieldAlert} titulo="Fotos con falta de EPP" tono={a?.fotosConFalta ? 'rojo' : 'verde'} valor={a ? a.fotosConFalta : '—'} sub={a ? `de ${a.fotos} revisadas` : ''} />
        <Kpi icono={MapPin} titulo="En zonas de riesgo" tono={a?.horasRiesgo ? 'ambar' : 'gris'} valor={config.zonasRiesgo?.length ? (a ? fmtHoras(a.horasRiesgo) : '—') : '—'} sub={config.zonasRiesgo?.length ? 'Horas-persona' : 'Marcá las zonas abajo'} />
      </div>

      {/* EPP */}
      <div className={`grid ${visibles.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-2 sm:gap-3`} data-seg-epp>
        {!visibles.length && <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-2xl px-4 py-3">No marcaste ningún elemento como obligatorio en esta obra: no se muestran faltas de EPP.</p>}
        {visibles.map((e) => {
          const x = a?.total[e.key], v = pct(x, MIN_MUESTRA), n = x ? x.si + x.no : 0, I = ICONO[e.key], on = elemento === e.key;
          return (
            <button key={e.key} type="button" onClick={() => setElemento(e.key)} className={`text-left bg-white border rounded-2xl p-3 sm:p-4 shadow-sm transition-colors ${on ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-400'}`}>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-xl grid place-items-center shrink-0" style={{ color: colorCumpl(v, meta), background: colorCumpl(v, meta) + '1f' }}><I size={17} /></span>
                <b className="text-sm text-gray-800 leading-tight">{e.label}</b>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{v != null ? `${r0(v)}%` : n ? <span className="text-base font-semibold text-gray-500">Pocos datos</span> : '—'}</div>
              <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v ?? 0}%`, background: colorCumpl(v, meta) }} /></div>
              <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
                {!x || !(x.si + x.no) ? 'Todavía no se pudo ver en ninguna persona.' : `${x.si} de ${x.si + x.no} personas donde se veía.`}
                {x && x.nv > 0 && <span className="block">En {x.nv} no se veía bien: no cuentan.</span>}
              </p>
            </button>
          );
        })}
      </div>

      {/* Cumplimiento */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">
        <Tarjeta titulo={`${el.label} ${unDia ? 'por hora' : 'por día'}`} icono={CheckCircle2} derecha={<button type="button" onClick={descargarCsv} disabled={!a} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900"><Download size={14} />CSV</button>}>
          {a && a.personas ? (
            <div className="h-56 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={datosGrafico} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd" tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={false} unit="%" />
                  <Tooltip formatter={(v, _n, it) => (v == null ? 'Pocos datos' : `${v}% con ${el.label.toLowerCase()} (${it?.payload?.n} personas)`)} labelStyle={{ fontWeight: 600 }} />
                  <ReferenceLine y={meta} stroke="#111827" strokeDasharray="4 4" label={{ value: `Meta ${meta}%`, position: 'insideTopRight', fontSize: 10, fill: '#111827' }} />
                  <Bar dataKey="v" radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false}>
                    {datosGrafico.map((d, i) => <Cell key={i} fill={colorCumpl(d.v, meta)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Vacio cargando={cargando || verificando} txt="Todavía no hay personas confirmadas en este período." />}
        </Tarjeta>

        <Tarjeta titulo={`${el.label}: día × hora`} icono={Clock}>
          {a && a.personas ? (
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="text-[10px] sm:text-[11px] border-separate" style={{ borderSpacing: 2 }}>
                <thead><tr><th />{HORAS_VISTA.map((h) => <th key={h} className="font-medium text-gray-500 w-7 sm:w-8">{h}</th>)}</tr></thead>
                <tbody>
                  {dias.slice(-14).map((k) => (
                    <tr key={k}>
                      <td className="pr-2 text-gray-500 whitespace-nowrap">{fmtDow(k)} {k.slice(8)}/{k.slice(5, 7)}</td>
                      {HORAS_VISTA.map((h) => <CeldaHora key={h} fotos={(verif?.dias.find((x) => x.dia === k)?.fotos || [])} h={h} elemento={elemento} meta={meta} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-3 flex-wrap mt-3 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><i className="w-3 h-3 rounded inline-block" style={{ background: '#10b981' }} />En la meta</span>
                <span className="flex items-center gap-1"><i className="w-3 h-3 rounded inline-block" style={{ background: '#f59e0b' }} />Cerca</span>
                <span className="flex items-center gap-1"><i className="w-3 h-3 rounded inline-block" style={{ background: '#ef4444' }} />Debajo</span>
                <span className="flex items-center gap-1"><i className="w-3 h-3 rounded inline-block bg-gray-100" />Sin gente o no se veía</span>
              </div>
            </div>
          ) : <Vacio cargando={cargando || verificando} txt="Sin datos para el mapa." />}
        </Tarjeta>
      </div>

      {/* Zonas de riesgo + eventos */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-3 sm:gap-4 items-start">
        <Tarjeta titulo="Zonas de alto riesgo" icono={AlertTriangle} derecha={<button type="button" onClick={() => setEditor(true)} disabled={!fotoZonas} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg px-2.5 py-1.5 disabled:opacity-40" data-seg-editar-zonas><Pencil size={13} />{config.zonasRiesgo?.length ? 'Editar zonas' : 'Marcar zonas'}</button>}>
          {fotoZonas ? (
            <div className="space-y-3">
              <div className="relative w-full rounded-xl overflow-hidden bg-gray-900" style={{ aspectRatio: `${fotoZonas.w} / ${fotoZonas.h}` }}>
                {fotoZonasUrl && <img src={fotoZonasUrl} alt="Foto de la obra con las zonas de riesgo" className="absolute inset-0 w-full h-full object-cover" />}
                <svg viewBox={`0 0 ${fotoZonas.w} ${fotoZonas.h}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
                  {(config.zonasRiesgo || []).map((z) => <polygon key={z.id} points={z.puntos.map(([x, y]) => `${x * fotoZonas.w},${y * fotoZonas.h}`).join(' ')} fill={COLOR_NIVEL[z.nivel] + '40'} stroke={COLOR_NIVEL[z.nivel]} strokeWidth={fotoZonas.w / 350} />)}
                  {fotoZonas.personas.filter(confirmada).map((p, i) => <rect key={i} x={p.box[0]} y={p.box[1]} width={p.box[2] - p.box[0]} height={p.box[3] - p.box[1]} fill="none" stroke={p.casco === 'no' ? '#ef4444' : '#10b981'} strokeWidth={fotoZonas.w / 220} rx={fotoZonas.w / 400} />)}
                </svg>
                <span className="absolute left-2 bottom-2 text-[10px] sm:text-[11px] text-white bg-black/60 rounded px-1.5 py-0.5">{fmtKey(diaDe(fotoZonas.ms))} {fmtHM(fotoZonas.ms)} · {fotoZonas.personas.filter(confirmada).length} personas</span>
              </div>
              {!config.zonasRiesgo?.length ? (
                <p className="text-sm text-gray-600">Marcá los lugares peligrosos (borde de losa, excavación, radio de grúa…) y te mostramos cuánta gente pasa por ahí y con qué protección.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
                  {a?.zonas.map((z) => (
                    <li key={z.zona.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 min-w-0"><i className="w-3 h-3 rounded-full shrink-0" style={{ background: COLOR_NIVEL[z.zona.nivel] }} /><span className="truncate">{z.zona.nombre}</span></span>
                      <span className="text-xs text-gray-600 whitespace-nowrap">{z.personas ? <>{fmtHoras(z.horas)} · {z.personas} {z.personas === 1 ? 'persona' : 'personas'}{z.faltas.casco.no ? <b className="text-red-600"> · {z.faltas.casco.no} sin casco</b> : ''}</> : 'Sin gente'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : <Vacio cargando={cargando || verificando} txt="Todavía no hay fotos verificadas en este período." />}
        </Tarjeta>

        <Tarjeta titulo="Faltas de EPP y zonas" icono={ScanSearch} derecha={
          <div className="flex gap-1 flex-wrap">
            {[['todos', 'Todo'], ...visibles.map((e) => [e.key, e.falta]), ['zona', 'En zona']].map(([k, t]) => <Chip key={k} on={filtro === k} onClick={() => { setFiltro(k); setVerEventos(12); }}>{t}</Chip>)}
          </div>
        }>
          {eventos.length ? (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-seg-eventos>
                {eventos.slice(0, verEventos).map((e, i) => (
                  <figure key={e.foto.id + i} className="rounded-lg overflow-hidden border border-gray-200 bg-white" title={e.p.nota}>
                    <Recorte foto={e.foto} box={e.p.box} borde={e.faltan.length ? '#ef4444' : COLOR_NIVEL[e.zona.nivel]} />
                    <figcaption className="px-1.5 py-1 text-[10px] sm:text-[11px] leading-tight">
                      <div className="font-semibold text-gray-700">{fmtKey(diaDe(e.foto.ms)).slice(0, 5)} {fmtHM(e.foto.ms)}</div>
                      {e.faltan.map((k) => <div key={k} className="text-red-600 font-semibold">{ELEMENTOS.find((x) => x.key === k).falta}</div>)}
                      {e.zona && <div className="truncate" style={{ color: COLOR_NIVEL[e.zona.nivel] }} title={e.zona.nombre}>{e.zona.nombre}</div>}
                    </figcaption>
                  </figure>
                ))}
              </div>
              {eventos.length > verEventos && <button type="button" onClick={() => setVerEventos(verEventos + 12)} className="mt-3 w-full text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl py-2 hover:bg-gray-50">Ver más ({eventos.length - verEventos})</button>}
              <p className="text-[11px] text-gray-400 mt-2">El recuadro marca a la persona revisada (puede haber otras en el recorte). Solo personas confirmadas y faltas que se ven con claridad.</p>
            </>
          ) : <Vacio cargando={cargando || verificando} txt={filtro === 'zona' && !config.zonasRiesgo?.length ? 'Marcá zonas de riesgo para ver quién entra.' : 'Sin faltas confirmadas en este período. 👍'} />}
        </Tarjeta>
      </div>

      {/* Personas (Re-ID) */}
      <Tarjeta
        titulo="Personas en obra"
        icono={Users}
        data-seg-personas
        derecha={rep?.pdfUrl && <a href={rep.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900"><FileText size={14} />PDF del día</a>}
      >
        {!personas ? <Vacio cargando txt="" /> : !rep ? (
          <div className="text-sm text-gray-600 space-y-2">
            <p>Todavía no hay reporte de personas para este período. El reporte se arma de noche y separa a cada persona con su recorte, la hora de entrada, la de salida y si tuvo casco.</p>
            {ultimoReporte && <button type="button" onClick={() => setDiaPersonas(ultimoReporte.fecha)} className="text-blue-600 font-semibold hover:underline">Ver el último reporte ({fmtKey(ultimoReporte.fecha)})</button>}
            {errorPersonas && <p className="text-xs text-red-600">No pudimos leer los reportes de personas.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              {(enPeriodo.length ? enPeriodo : [rep]).slice(-10).map((d) => <Chip key={d.fecha} on={d.fecha === rep.fecha} onClick={() => setDiaPersonas(d.fecha)}>{fmtDow(d.fecha)} {fmtKey(d.fecha).slice(0, 5)}</Chip>)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {[
                ['Personas', rep.resumen.personasDistintas],
                ['Con casco siempre', `${resumenPersonas.siempreCasco} de ${resumenPersonas.total}`],
                ['Primera / última', `${rep.resumen.primera || '—'} – ${rep.resumen.ultima || '—'}`],
                ['Permanencia media', fmtPermanencia(resumenPersonas.permanenciaProm)],
              ].map(([t, v]) => (
                <div key={t} className="bg-gray-50 rounded-xl px-2 py-2.5"><div className="text-[11px] text-gray-500">{t}</div><div className="text-sm sm:text-base font-bold text-gray-800 mt-0.5">{v}</div></div>
              ))}
            </div>
            {!rep.personas.length ? <p className="text-sm text-gray-500">Ese día no se vio a nadie con claridad.</p> : (
              <ul className="space-y-3">
                {rep.personas.map((p, i) => (
                  <li key={p.id} className="border border-gray-200 rounded-xl overflow-hidden" data-seg-persona>
                    <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
                        <b className="text-sm text-gray-800 truncate">{p.nombre || `Persona ${i + 1}`}</b>
                        <BadgeCasco pct={p.cascoPct} meta={meta} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-600">
                        <span className="flex items-center gap-1" title="Primera vez que se la vio"><LogIn size={13} className="text-emerald-600" />{p.primera || '—'}</span>
                        <span className="flex items-center gap-1" title="Última vez que se la vio"><LogOut size={13} className="text-red-500" />{p.ultima || '—'}</span>
                        <span className="flex items-center gap-1" title="Permanencia"><Clock size={13} />{fmtPermanencia(p.permanenciaMin)}</span>
                      </div>
                    </div>
                    {p.stripUrl ? (
                      <div className="overflow-x-auto bg-gray-100"><img src={p.stripUrl} alt={`Recortes de la persona ${i + 1} durante el día`} loading="lazy" className="h-28 sm:h-36 max-w-none" /></div>
                    ) : <div className="text-xs text-gray-400 px-3 py-4">Sin recortes guardados.</div>}
                    <div className="px-3 py-1.5 text-[11px] text-gray-500">{p.detecciones} {p.detecciones === 1 ? 'aparición' : 'apariciones'}</div>
                  </li>
                ))}
              </ul>
            )}
            {rep.fichajes?.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-1">Fichajes del día</div>
                <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded-xl">
                  {rep.fichajes.map((f, i) => <li key={i} className="flex justify-between gap-2 px-3 py-2"><span className="truncate">{f.nombre || 'Sin nombre'}</span><span className="text-gray-500 whitespace-nowrap">{f.ingreso || '—'} → {f.egreso || '—'}</span></li>)}
                </ul>
              </div>
            )}
            <p className="text-[11px] text-gray-400 flex gap-1"><Info size={12} className="shrink-0 mt-0.5" />Cada día la IA separa a las personas por su aspecto: el número de persona no es el mismo de un día a otro. No reconocemos caras.</p>
          </div>
        )}
      </Tarjeta>

      {/* Recomendaciones y alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-3 sm:gap-4">
        <Tarjeta titulo="Qué mirar" icono={Info}>
          {consejos.length ? (
            <ul className="space-y-2">
              {consejos.map((c, i) => {
                const I = c.tono === 'ok' ? CheckCircle2 : c.tono === 'mal' ? XCircle : c.tono === 'aviso' ? AlertTriangle : Info;
                const col = c.tono === 'ok' ? 'text-emerald-600' : c.tono === 'mal' ? 'text-red-600' : c.tono === 'aviso' ? 'text-amber-600' : 'text-blue-600';
                return <li key={i} className="flex gap-2 text-sm text-gray-700"><I size={16} className={`${col} shrink-0 mt-0.5`} /><span>{c.txt}</span></li>;
              })}
            </ul>
          ) : <Vacio cargando={cargando || verificando} txt="Cuando haya personas confirmadas te vamos a marcar acá lo importante." />}
        </Tarjeta>
        <Tarjeta titulo="Alertas" icono={Bell}>
          <p className="text-sm text-gray-600">Recibí un WhatsApp o mail cuando el uso de casco baja de lo que definas, o cuando la obra arranca tarde o con menos gente.</p>
          <button type="button" onClick={() => onIrA && onIrA('Alertas')} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2"><Bell size={15} />Configurar alertas de EPP</button>
        </Tarjeta>
      </div>

      {editor && (
        <ZonasRiesgoEditor
          feedId={feedId}
          photo={fotoZonas}
          url={fotoZonasUrl}
          config={config}
          onClose={() => setEditor(false)}
          onGuardado={(c) => { setConfig(c); setEditor(false); }}
        />
      )}
    </div>
  );
}

/** Celda del mapa día × hora: % del elemento entre las personas confirmadas de esa hora. */
function CeldaHora({ fotos, h, elemento, meta }) {
  let si = 0, no = 0;
  for (const f of fotos) {
    if (new Date(f.ms - 3 * 3600e3).getUTCHours() !== h) continue;
    for (const p of f.personas) { if (!confirmada(p)) continue; if (p[elemento] === 'si') si += 1; else if (p[elemento] === 'no') no += 1; }
  }
  const v = si + no ? (100 * si) / (si + no) : null;
  return <td title={v == null ? 'Sin datos' : `${Math.round(v)}% (${si} de ${si + no})`} className="h-6 sm:h-7 rounded" style={{ background: v == null ? '#f3f4f6' : colorCumpl(v, meta), opacity: v == null ? 1 : 0.45 + Math.min(1, (si + no) / 6) * 0.55 }} />;
}

function Vacio({ cargando, txt }) {
  if (cargando) return <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center"><Loader2 size={15} className="animate-spin" />Cargando…</div>;
  return <p className="text-sm text-gray-500 py-4 text-center">{txt}</p>;
}
